import { settingsCardStyle, settingsDescriptionStyle, settingsHeadingStyle } from './settingsStyles';
// ----- Phase 5.X-settings-schema-ui — Current Configuration panel -----
//
// Collapsible summary showing all schema keys with their currently-
// effective values (post 3-tier merge) and the scope the value came from
// (default / user / org). Click a row → scrolls the corresponding editor
// row into view + highlights it.
//
// Purely informational — no edits happen in this panel.

import type { CSSProperties, ReactElement } from 'react';
import { SETTINGS_SCHEMA, type SettingsSchemaEntry } from '@/lib/settings-schema';

/** Where a key's effective value came from after the 3-tier merge. */
export type EffectiveScope = 'default' | 'user' | 'org' | 'submission';

export interface EffectiveValueInfo {
  readonly value: boolean | number | string;
  readonly scope: EffectiveScope;
}

export interface CurrentConfigurationPanelProps {
  /** Map of key → effective value + scope source. Schema keys not present here fall back to defaults. */
  readonly effective: Readonly<Record<string, EffectiveValueInfo>>;
  /** Called when the user clicks a row — page scrolls/highlights the editor. */
  readonly onRowClick?: (key: string) => void;
}

const rowStyle: CSSProperties = {
  display: 'flex',
  breakInside: 'avoid',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '6px 0',
  borderTop: '1px solid var(--border, #e5e7eb)',
  cursor: 'pointer',
};

const labelTextStyle: CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  color: 'var(--text, #111827)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const valueStyle: CSSProperties = {
  flex: '0 0 auto',
  minWidth: 0,
  maxWidth: '45%',
  overflowWrap: 'anywhere',
  fontWeight: 600,
  color: 'var(--accent, #2563eb)',
};

const scopeBadgeBase: CSSProperties = {
  flex: '0 0 auto',
  padding: '1px 6px',
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
};

const scopeBadgeStyles: Record<EffectiveScope, CSSProperties> = {
  default: {
    ...scopeBadgeBase,
    background: 'var(--surface2, #f3f4f6)',
    color: 'var(--text-muted, #6b7280)',
    border: '1px solid var(--border, #e5e7eb)',
  },
  user: {
    ...scopeBadgeBase,
    background: '#dbeafe',
    color: '#1e40af',
    border: '1px solid #93c5fd',
  },
  org: {
    ...scopeBadgeBase,
    background: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fcd34d',
  },
  submission: {
    ...scopeBadgeBase,
    background: '#dcfce7',
    color: '#15803d',
    border: '1px solid #86efac',
  },
};

function renderValue(entry: SettingsSchemaEntry, value: boolean | number | string): string {
  if (entry.controlType === 'toggle') return value ? 'On' : 'Off';
  if (entry.controlType === 'select') {
    const opt = entry.options?.find((o) => o.value === String(value));
    return opt ? opt.label : String(value);
  }
  // slider
  const unit = entry.unit ?? '';
  return `${value}${unit}`;
}

export function CurrentConfigurationPanel({
  effective,
  onRowClick,
}: CurrentConfigurationPanelProps): ReactElement {
  return (
    <aside aria-label="Current Configuration" style={{ minWidth: 0 }}>
      <details open style={settingsCardStyle} data-testid="current-configuration-panel">
        <summary style={{ cursor: 'pointer', ...settingsHeadingStyle }}>
          <h3 style={{ ...settingsHeadingStyle, display: 'inline' }}>Current Configuration</h3>
        </summary>
        <p style={{ ...settingsDescriptionStyle, marginBottom: 16 }}>
          Effective values after 3-tier merge (default → user → org).
        </p>
      <div style={{ columnCount: 2, columnWidth: 260, columnGap: 24 }}>
      {SETTINGS_SCHEMA.map((entry) => {
        const info = effective[entry.key] ?? {
          value: entry.defaultValue,
          scope: 'default' as const,
        };
        return (
          <div
            key={entry.key}
            style={rowStyle}
            data-testid={`config-summary-row-${entry.key}`}
            onClick={() => onRowClick?.(entry.key)}
            role={onRowClick ? 'button' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onRowClick(entry.key);
              }
            }}
          >
            <span style={labelTextStyle} title={entry.label}>
              {entry.label}
            </span>
            <span style={valueStyle} data-testid={`config-summary-value-${entry.key}`}>
              {renderValue(entry, info.value)}
            </span>
            <span
              style={scopeBadgeStyles[info.scope]}
              data-testid={`config-summary-scope-${entry.key}`}
            >
              {info.scope}
            </span>
          </div>
        );
      })}
      </div>
      </details>
    </aside>
  );
}
